import type {
  ProvinceCreateInput,
  ProvinceUpdateInput,
  DistrictCreateInput,
  DistrictUpdateInput,
} from "@/modules/geo/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { geoApi } from "./client";

export const geoKeys = {
  all: ["geo"] as const,
  provinces: {
    all: ["geo", "provinces"] as const,
    list: (q: Partial<OffsetPageQueryDTO>) =>
      ["geo", "provinces", "list", q] as const,
    detail: (id: string) => ["geo", "provinces", "detail", id] as const,
  },
  districts: {
    all: ["geo", "districts"] as const,
    list: (q: Partial<OffsetPageQueryDTO>) =>
      ["geo", "districts", "list", q] as const,
    detail: (id: string) => ["geo", "districts", "detail", id] as const,
  },
  zones: ["geo", "zones"] as const,
};

export function useProvincesQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: geoKeys.provinces.list(q),
    queryFn: () => geoApi.provinces.list(q),
  });
}

export function useProvinceQuery(id: string) {
  return useQuery({
    queryKey: geoKeys.provinces.detail(id),
    queryFn: () => geoApi.provinces.get(id),
    enabled: !!id,
  });
}

export function useCreateProvince() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProvinceCreateInput) => geoApi.provinces.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: geoKeys.provinces.all });
      toast.success("ສ້າງແຂວງສໍາເລັດ");
    },
  });
}

export function useUpdateProvince(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProvinceUpdateInput) =>
      geoApi.provinces.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: geoKeys.provinces.detail(id) });
      qc.invalidateQueries({ queryKey: geoKeys.provinces.all });
      toast.success("ແກ້ໄຂແຂວງສໍາເລັດ");
    },
  });
}

export function useDeleteProvince() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => geoApi.provinces.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: geoKeys.provinces.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

export function useToggleProvinceEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geoApi.provinces.toggleEnabled(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: geoKeys.provinces.detail(id) });
      qc.invalidateQueries({ queryKey: geoKeys.provinces.all });
    },
  });
}

export function useDistrictsQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: geoKeys.districts.list(q),
    queryFn: () => geoApi.districts.list(q),
  });
}

export function useDistrictQuery(id: string) {
  return useQuery({
    queryKey: geoKeys.districts.detail(id),
    queryFn: () => geoApi.districts.get(id),
    enabled: !!id,
  });
}

export function useCreateDistrict() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DistrictCreateInput) => geoApi.districts.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: geoKeys.districts.all });
      toast.success("ສ້າງເມືອງສໍາເລັດ");
    },
  });
}

export function useUpdateDistrict(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DistrictUpdateInput) =>
      geoApi.districts.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: geoKeys.districts.detail(id) });
      qc.invalidateQueries({ queryKey: geoKeys.districts.all });
      toast.success("ແກ້ໄຂເມືອງສໍາເລັດ");
    },
  });
}

export function useDeleteDistrict() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => geoApi.districts.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: geoKeys.districts.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

export function useToggleDistrictEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => geoApi.districts.toggleEnabled(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: geoKeys.districts.detail(id) });
      qc.invalidateQueries({ queryKey: geoKeys.districts.all });
    },
  });
}

export function useZonesQuery() {
  return useQuery({
    queryKey: geoKeys.zones,
    queryFn: () => geoApi.zones.list(),
  });
}
